import { useEffect, useMemo } from "react"

import { Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import EmptyComponent from "../../../../Components/Empty"
import { RoutesPaths } from "../../../../constants/user-routes"
import { CardTotalPanel } from "../../../DashboardPage/components/CardTotalPanel"
import { useOpenDeflectPage } from "../../hooks/useDeflectFilters"
import { DEFLECT_SECTION } from "../../redux/deflectAnalytics.constants"
import {
  selectDeflectFetching,
  selectDeflectFirstLoad,
  selectDeflectSection
} from "../../redux/deflectAnalytics.slice"
import Section from "../Section"
import CountriesPanelSkeleton from "./CountriesPanelSkeleton"
import CountryChart from "./CountryChart"

import styles from "./countriesPanel.module.less"

// On a phone the totals sit right on the chart: the chart keeps ~11px of its own above the first
// grid line, so this lands near the page's 12px step instead of 32px + 11px.
const MOBILE_TOTAL_STYLE = { marginBottom: 0 }

// "Top countries": the Alerts by Country card, ported for lookups. Top 10 by count, out of the
// same by-country/ answer the Countries page reads.
const CountriesPanel = ({ getData, range }) => {
  const { t } = useTranslation()
  const openPage = useOpenDeflectPage()
  const byCountry = useSelector(selectDeflectSection(DEFLECT_SECTION.BY_COUNTRY))
  const fetching = useSelector(selectDeflectFetching(DEFLECT_SECTION.BY_COUNTRY))
  const firstLoad = useSelector(selectDeflectFirstLoad(DEFLECT_SECTION.BY_COUNTRY))
  const isMobile = useSelector((s) => s.nav.isMobile)

  useEffect(() => {
    getData(DEFLECT_SECTION.BY_COUNTRY)
  }, [getData])

  const rows = useMemo(
    () =>
      (byCountry?.result ?? []).slice(0, 10).map((row) => ({
        key: row.country_code,
        count: row.lookups_count,
        value: row.order_amount
      })),
    [byCountry]
  )
  // Header totals reconcile with the bars below: the SAME displayed rows (top 10), not the
  // whole summary, otherwise the header overshoots the visible bars.
  const totalCount = useMemo(() => rows.reduce((a, r) => a + (r.count || 0), 0), [rows])
  const totalVolume = useMemo(() => rows.reduce((a, r) => a + (r.value || 0), 0), [rows])

  return (
    <Section
      title={t("Top countries")}
      tooltip={t("Where your bank inquiries originate, by cardholder country.")}
      onViewAll={rows.length ? () => openPage(RoutesPaths.deflect.analytics_countries) : undefined}
      minHeight={536}
      className={styles.fillCard}
    >
      {firstLoad ? (
        <CountriesPanelSkeleton />
      ) : (
        <Spin spinning={fetching} wrapperClassName={styles.spin}>
          {rows.length ? (
            <>
              <CardTotalPanel
                count={totalCount}
                volume={totalVolume}
                style={isMobile ? MOBILE_TOTAL_STYLE : undefined}
              />
              <div className={styles.chart}>
                <CountryChart rows={rows} range={range} />
              </div>
            </>
          ) : (
            <EmptyComponent type="no_data_available" />
          )}
        </Spin>
      )}
    </Section>
  )
}

export default CountriesPanel
